import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import {
  FaUser,
  FaEnvelope,
  FaBuilding,
  FaBriefcase,
  FaPhone,
  FaCalendarAlt,
  FaIdCard,
  FaHistory,
  FaMapMarkerAlt,
  FaUniversity,
  FaCreditCard,
  FaFileInvoiceDollar,
  FaHashtag,
  FaChartLine,
  FaGift,
  FaBullseye,
  FaTrophy,
  FaLock,
  FaUserClock,
  FaEye,
} from "react-icons/fa";
import toast from "react-hot-toast";
import { fmtINR, parseCTCNumber, computeGrossBreakdown } from "../utils/salaryUtils";
import { resolveEmployeeRecordRef } from "../utils/employeeRecord";
import { getProbationInfo } from "../utils/probation";
import AppraisalModal from "../components/AppraisalModal";
import "./EmployeeProfile.css";

const EmployeeProfile = ({ employee, onAppraisalDone }) => {
  const { userData: currentUser } = useAuth();
  // Show appraisal button only when admin/superadmin is viewing another employee's profile
  const canAppraise =
    (currentUser?.role === "superadmin" || currentUser?.role === "admin") &&
    employee?.id !== currentUser?.uid &&
    employee?.employeeId !== currentUser?.employeeId;

  const isPrivileged = currentUser?.role === "superadmin" || currentUser?.role === "admin";
  // Entries written before the wording change
  const isLegacyEdit = (a) => String(a.note || "").startsWith("Salary breakdown edited directly");

  const [showAppraisalModal, setShowAppraisalModal] = useState(false);
  const [compHistory, setCompHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showGrossBreakdown, setShowGrossBreakdown] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Reset image loaded state whenever the viewed employee changes
  useEffect(() => {
    if (!employee?.photoURL) {
      setImgLoaded(true); // no real photo to wait for, skip spinner
      return;
    }
    setImgLoaded(false);
    // Fallback: dismiss spinner after 0.8s in case onLoad doesn't fire (e.g. cached image)
    const t = setTimeout(() => setImgLoaded(true), 800);
    return () => clearTimeout(t);
  }, [employee?.id, employee?.photoURL]);

  useEffect(() => {
    if (!employee?.employeeId) return;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const { snap: recSnap } = await resolveEmployeeRecordRef(employee.employeeId, employee.uid || employee.id);
        const rec = recSnap.exists() ? recSnap.data() : {};
        const q = query(
          collection(db, "salary"),
          where("uid", "==", employee.uid || employee.employeeId),
          orderBy("payDate", "desc"),
          limit(24)
        );
        const paySnap = await getDocs(q);
        const payslips = paySnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCompHistory({
          appraisalLog: Array.isArray(rec.appraisalLog) ? rec.appraisalLog : [],
          eventLog:     Array.isArray(rec.salaryEventLog) ? rec.salaryEventLog : [],
          roleLog:      Array.isArray(rec.roleLog) ? rec.roleLog : [],
          payslips,
          // Fixed gross breakdown — null until admin saves one (no formula fallback)
          salaryBreakdown:    rec.salaryBreakdown && Number(rec.salaryBreakdown.basic) > 0 ? rec.salaryBreakdown : null,
          variableComponents: rec.variableComponents || {},
          grossMonthlySalary: rec.grossMonthlySalary || employee.grossMonthlySalary || "",
          annualCTC:          rec.annualCTC || employee.annualCTC || "",
        });
      } catch (e) { console.warn("EmployeeProfile fetchHistory:", e); }
      setHistoryLoading(false);
    };
    fetchHistory();
  }, [employee?.employeeId, employee?.uid, refreshKey]);

  // ── Past employees: generate a fixed breakdown from their existing gross salary ──
  const handleBackfillBreakdown = async () => {
    const grossNum = parseCTCNumber(String(compHistory?.grossMonthlySalary || "0"));
    if (!grossNum || grossNum <= 0) {
      toast.error("No existing gross salary on file to generate a breakdown from.");
      return;
    }
    const suggested = computeGrossBreakdown(grossNum);
    if (!suggested) return;
    setBackfilling(true);
    try {
      const empRecordId = employee.employeeId || employee.id;
      const { ref } = await resolveEmployeeRecordRef(empRecordId, employee.uid || employee.id);
      await setDoc(ref, {
        employeeId: empRecordId,
        uid: employee.uid || employee.id || "",
        salaryBreakdown: suggested,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCompHistory(prev => prev ? { ...prev, salaryBreakdown: suggested } : prev);
      setShowGrossBreakdown(true);
      toast.success("Fixed salary breakdown generated and saved from existing gross salary.");
    } catch (e) {
      console.error("handleBackfillBreakdown:", e);
      toast.error("Failed to save breakdown: " + e.message);
    } finally {
      setBackfilling(false);
    }
  };

  const handleAppraisalDone = (entry) => {
    // Optimistically add the new entry and trigger a re-fetch
    setCompHistory(prev => prev ? ({
      ...prev,
      appraisalLog: [...(prev.appraisalLog || []), entry],
    }) : prev);
    setRefreshKey(k => k + 1);
    if (onAppraisalDone) onAppraisalDone(entry);
  };

  const handlePromoteDone = (entry) => {
    setCompHistory(prev => prev ? ({
      ...prev,
      roleLog: [...(prev.roleLog || []), entry],
    }) : prev);
    setRefreshKey(k => k + 1);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);

  if (!employee)
    return <div className="loading-state">Profile data unavailable.</div>;

  // ── Probation detection ────────────────────────────────────────────────────
  const probationInfo = getProbationInfo(employee.joiningDate, employee.probationExtended);

  const calculateExperience = (joiningDate) => {
    if (!joiningDate) return "N/A";
    try {
      const start = new Date(joiningDate);
      const end = new Date();
      if (isNaN(start.getTime())) return "Invalid Date";
      let years = end.getFullYear() - start.getFullYear();
      let months = end.getMonth() - start.getMonth();
      if (months < 0) {
        years--;
        months += 12;
      }
      if (years === 0 && months === 0) return "Less than a month";
      const yearString =
        years > 0 ? `${years} year${years > 1 ? "s" : ""}` : "";
      const monthString =
        months > 0 ? `${months} month${months > 1 ? "s" : ""}` : "";
      return [yearString, monthString].filter(Boolean).join(" ");
    } catch {
      return "N/A";
    }
  };

  const InfoRow = ({ icon, label, value }) => (
    <div className="info-row">
      <span className="info-row-label">
        {icon && <span className="info-row-icon">{icon}</span>}
        {label}
      </span>
      <span className="info-row-value">{value || "—"}</span>
    </div>
  );

  return (
    <div className="employee-profile-card">
      <header className="profile-header">
        <div className="profile-photo-wrap">
          {!imgLoaded && (
            <div className="profile-photo-spinner" aria-label="Loading photo">
              <div className="profile-photo-spinner-ring" />
            </div>
          )}
          <img
            key={employee?.id}
            src={employee.photoURL || "/default-avatar.png"}
            alt="profile"
            className="profile-photo"
            style={{ display: imgLoaded ? "block" : "none" }}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgLoaded(true)}
          />
        </div>
        <div className="profile-name-area">
          <h1>{employee.name || "Unnamed Employee"}</h1>
          <p className="profile-role-title">{employee.position || "Staff Member"}</p>
          <div className="profile-chips">
            {employee.department && <span className="pchip dept">{employee.department}</span>}
            {employee.employeeId && <span className="pchip id"><FaIdCard size={11} /> {employee.employeeId}</span>}
            {employee.joiningDate && <span className="pchip join"><FaCalendarAlt size={11} /> Joined {employee.joiningDate}</span>}
            <span className="pchip manager"><FaUserClock size={11} /> Reports to: {employee.reportingManager || 'Hanumat Lal Vishwakarma (Founder & Managing Director)'}</span>
          </div>
        </div>
        {canAppraise && (
          <button
            className="appraisal-btn-profile"
            onClick={() => setShowAppraisalModal(true)}
            title="Record salary appraisal for this employee"
          >
            <FaTrophy /> Appraise
          </button>
        )}

        {/* ── Probation badge ── */}
        {probationInfo.inProbation && (
          <div className="probation-badge" title={`Probation ends on ${probationInfo.endDate?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}>
            <span className="probation-badge-icon"><FaLock size={10} /></span>
            <span className="probation-badge-label">
              {probationInfo.extended ? 'Extended Probation' : 'In Probation'}
            </span>
            <span className="probation-badge-days">{probationInfo.daysLeft}d left</span>
          </div>
        )}
      </header>

      {/* ══════════ INFO SECTIONS ══════════ */}
      <div className="profile-sections">
        {/* Contact */}
        <section className="pg-section">
          <h5 className="pg-section-title"><FaEnvelope /> Contact Information</h5>
          <InfoRow icon={<FaEnvelope />}     label="Email"    value={employee.email} />
          <InfoRow icon={<FaPhone />}        label="Phone"    value={employee.phone} />
          <InfoRow icon={<FaMapMarkerAlt />} label="Location" value={employee.location} />
        </section>

        {/* Job Info */}
        <section className="pg-section">
          <h5 className="pg-section-title"><FaBriefcase /> Job Information</h5>
          <InfoRow label="Employee ID"   value={employee.employeeId} />
          <InfoRow label="Department"    value={employee.department} />
          <InfoRow label="Position"      value={employee.position} />
          <InfoRow label="Joining Date"  value={employee.joiningDate} />
          <InfoRow label="Experience"    value={calculateExperience(employee.joiningDate)} />
          <InfoRow icon={<FaUserClock />} label="Reporting Manager" value={employee.reportingManager || "Hanumat Lal Vishwakarma (Founder & Managing Director)"} />
          <div className="info-row">
            <span className="info-row-label">Monthly Gross</span>
            <span className="info-row-value gross-value-cell">
              {compHistory?.grossMonthlySalary ? `₹${fmtINR(parseCTCNumber(String(compHistory.grossMonthlySalary)))}` : "—"}
              {compHistory?.grossMonthlySalary && (
                <button
                  type="button"
                  className="gross-eye-btn"
                  onClick={() => setShowGrossBreakdown(true)}
                  title={compHistory?.salaryBreakdown ? "View salary breakdown" : "No fixed breakdown saved yet"}
                >
                  <FaEye />
                </button>
              )}
            </span>
          </div>
          <InfoRow label="Total CTC"     value={compHistory?.annualCTC ? `₹${fmtINR(parseCTCNumber(String(compHistory.annualCTC)))}` : "—"} />
        </section>

        {/* Bank & Statutory */}
        <section className="pg-section">
          <h5 className="pg-section-title"><FaUniversity /> Bank &amp; Statutory</h5>
          <InfoRow label="Bank Name"   value={employee.bankName} />
          <InfoRow label="Account No." value={employee.bankAccountNumber} />
          <InfoRow label="IFSC Code"   value={employee.ifscCode} />
          <InfoRow label="PAN Number"  value={employee.panNumber} />
          <InfoRow label="PF Number"   value={employee.pfNumber} />
          <InfoRow label="UAN"         value={employee.uanNumber} />
        </section>
      </div>

      {/* ══════════ SALARY CHANGE HISTORY ══════════ */}
      <div className="comp-history-section">
        <h3 className="comp-history-title"><FaChartLine /> Salary Change History</h3>

        {historyLoading && <p className="comp-history-loading">Loading history…</p>}

        {/* ── Salary Revisions block: always shown (joining row uses employee prop only) ── */}
        <div className="comp-history-block">
          <h4>Salary Revisions &amp; Appraisals</h4>
          <div className="comp-revisions-scroll">
            <table className="comp-events-table comp-revisions-table">
              <thead>
                <tr>
                  <th>Effective Date</th>
                  <th>Total CTC</th>
                  <th>Monthly Gross</th>
                  <th>Increment (₹)</th>
                  <th>Appraisal %</th>
                  <th>Retention Bonus</th>
                  <th>Revised By</th>
                  {isPrivileged && <th>Remarks</th>}
                </tr>
              </thead>
              <tbody>
                {compHistory && [...compHistory.appraisalLog].reverse()
                  .filter(a => parseCTCNumber(a.newCTC || "0") > 0 || parseCTCNumber(a.newMonthlyGross || "0") > 0)
                  .map((a, i) => {
                  const oldN     = parseCTCNumber(a.oldCTC || "0");
                  const newN     = parseCTCNumber(a.newCTC || "0");
                  const incrAmt  = oldN > 0 ? newN - oldN : 0;
                  const pct      = oldN > 0 ? (((newN - oldN) / oldN) * 100).toFixed(1) : null;
                  const tranche  = compHistory?.eventLog?.find(
                    ev => ev.type === "retention_tranche" && ev.payDate === a.date
                  );
                  return (
                    <tr key={i}>
                      <td><strong>{a.date}</strong></td>
                      <td>
                        ₹{fmtINR(newN)}/yr
                        {oldN > 0 && <div className="comp-tbl-sub">was ₹{fmtINR(oldN)}</div>}
                      </td>
                      <td>
                        {a.newMonthlyGross
                          ? `₹${fmtINR(parseCTCNumber(a.newMonthlyGross))}/mo`
                          : "—"}
                        {a.oldMonthlyGross && a.oldMonthlyGross !== "0" && (
                          <div className="comp-tbl-sub">was ₹{fmtINR(parseCTCNumber(a.oldMonthlyGross))}</div>
                        )}
                      </td>
                      <td className={incrAmt > 0 ? "comp-tbl-pos" : ""}>
                        {incrAmt > 0 ? `+₹${fmtINR(incrAmt)}/yr` : "—"}
                      </td>
                      <td className={pct !== null ? "comp-tbl-pos" : ""}>
                        {pct !== null ? `▲ ${Math.abs(parseFloat(pct))}%` : "—"}
                      </td>
                      <td>{tranche ? `₹${fmtINR(tranche.amount)}` : <span className="comp-tl-na">—</span>}</td>
                      <td>{isLegacyEdit(a) ? "HR" : (a.revisedBy || "—")}</td>
                      {isPrivileged && <td>{isLegacyEdit(a) ? "Salary structure revised" : (a.note || "—")}</td>}
                    </tr>
                  );
                })}

                {/* Joining row — always rendered, baseline entry */}
                <tr className="comp-tbl-joining-row">
                  <td><strong>{employee.joiningDate || "—"}</strong></td>
                  <td>
                    {parseCTCNumber(employee.annualCTC || "") > 0
                      ? `₹${fmtINR(parseCTCNumber(employee.annualCTC))}/yr`
                      : "—"}
                  </td>
                  <td>
                    {parseCTCNumber(employee.grossMonthlySalary || "") > 0
                      ? `₹${fmtINR(parseCTCNumber(employee.grossMonthlySalary))}/mo`
                      : "—"}
                  </td>
                  <td><span className="comp-tl-na">—</span></td>
                  <td><span className="comp-tl-na">—</span></td>
                  <td><span className="comp-tl-na">—</span></td>
                  <td>Offer Letter</td>
                  {isPrivileged && <td>Initial package at joining 🎉</td>}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {!historyLoading && compHistory && (
          <>
            {/* ── Special Payments (Retention Bonus / Joining Bonus / Variable) ── */}
            {compHistory.eventLog.length > 0 && (
              <div className="comp-history-block">
                <h4>Special &amp; One-Time Payments</h4>
                <table className="comp-events-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Month</th>
                      <th>Pay Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...compHistory.eventLog].reverse().map((ev, i) => (
                      <tr key={i}>
                        <td>
                          <span className="comp-evt-type-cell">
                            {ev.type === "retention_tranche"  && <FaChartLine  className="comp-evt-icon esop"     />}
                            {ev.type === "joining_bonus" && <FaGift        className="comp-evt-icon joining"  />}
                            {ev.type === "variable_pay"  && <FaBullseye    className="comp-evt-icon variable" />}
                            {!["retention_tranche","joining_bonus","variable_pay"].includes(ev.type) && (
                              <FaFileInvoiceDollar className="comp-evt-icon other" />
                            )}
                            <span className="comp-evt-type-label">
                              {ev.type === "retention_tranche"  && "Retention Bonus"}
                              {ev.type === "joining_bonus" && "Joining Bonus"}
                              {ev.type === "variable_pay"  && "Variable Pay"}
                              {!["retention_tranche","joining_bonus","variable_pay"].includes(ev.type) && "Other"}
                            </span>
                          </span>
                        </td>
                        <td>{ev.label}</td>
                        <td className="comp-evt-amount">₹{fmtINR(ev.amount)}</td>
                        <td>{ev.month}</td>
                        <td>{ev.payDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Monthly Payslip Summary ── */}
            {compHistory.payslips.length > 0 && (
              <div className="comp-history-block">
                <h4>Monthly Salary Records</h4>
                <table className="comp-events-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Pay Date</th>
                      <th>Gross</th>
                      <th>Deductions</th>
                      <th>Net (Take-Home)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compHistory.payslips.map((p) => (
                      <tr key={p.id}>
                        <td>{p.month}</td>
                        <td>{p.payDate}</td>
                        <td>{formatCurrency(p.grossSalary)}</td>
                        <td>{formatCurrency(p.totalDeductions)}</td>
                        <td className="comp-evt-amount">{formatCurrency(p.netSalary)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!compHistory.appraisalLog.length && !compHistory.eventLog.length && !compHistory.payslips.length && (
              <p className="comp-history-empty">No compensation records found yet.</p>
            )}
          </>
        )}
      </div>

      {/* ══════════ ROLE UPDATE / PROMOTIONS ══════════ */}
      <div className="comp-history-section" style={{ marginTop: '1.5rem' }}>
        <h3 className="comp-history-title"><FaUserClock /> Role Update / Promotions</h3>
        {historyLoading && <p className="comp-history-loading">Loading history…</p>}
        <div className="comp-history-block">
          <h4>Role &amp; Promotion History</h4>
          <div className="comp-revisions-scroll">
            <table className="comp-events-table comp-revisions-table">
              <thead>
                <tr>
                  <th>Effective Date</th>
                  <th>Previous Role</th>
                  <th>New Role</th>
                  <th>Position Change</th>
                  <th>Updated By</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {compHistory && [...(compHistory.roleLog || [])].reverse().map((r, i) => (
                  <tr key={i}>
                    <td><strong>{r.date}</strong></td>
                    <td>
                      {r.oldRole
                        ? <span style={{ textTransform: 'capitalize' }}>{r.oldRole}</span>
                        : <span className="comp-tl-na">—</span>}
                    </td>
                    <td>
                      <span style={{ textTransform: 'capitalize', fontWeight: 600, color: '#4F46E5' }}>
                        {r.newRole}
                      </span>
                    </td>
                    <td>
                      {r.newPosition && r.newPosition !== r.oldPosition
                        ? <span>{r.oldPosition || '—'} → <strong>{r.newPosition}</strong></span>
                        : <span className="comp-tl-na">—</span>}
                    </td>
                    <td>{r.promotedBy || '—'}</td>
                    <td>{r.note || '—'}</td>
                  </tr>
                ))}
                {/* Joining row — always rendered as the baseline */}
                <tr className="comp-tbl-joining-row">
                  <td><strong>{employee.joiningDate || '—'}</strong></td>
                  <td><span className="comp-tl-na">—</span></td>
                  <td>
                    <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                      {employee.role || 'employee'}
                    </span>
                  </td>
                  <td>{employee.position || <span className="comp-tl-na">—</span>}</td>
                  <td>Offer Letter</td>
                  <td>Initial role at joining 🎉</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Gross Salary Breakdown modal */}
      {showGrossBreakdown && (
        <div className="gross-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowGrossBreakdown(false)}>
          <div className="gross-modal">
            <button type="button" className="gross-modal-close" onClick={() => setShowGrossBreakdown(false)} title="Close">✕</button>
            <h3 className="gross-modal-title">Salary Breakdown — {employee.name}</h3>

            {compHistory?.salaryBreakdown ? (
              <>
                <div className="gross-modal-section-label">Monthly Gross Components</div>
                <div className="gross-modal-grid">
                  <div className="gross-modal-item"><span>Basic</span><strong>₹{fmtINR(compHistory.salaryBreakdown.basic)}</strong></div>
                  <div className="gross-modal-item"><span>HRA</span><strong>₹{fmtINR(compHistory.salaryBreakdown.hra)}</strong></div>
                  <div className="gross-modal-item"><span>LTA</span><strong>₹{fmtINR(compHistory.salaryBreakdown.lta)}</strong></div>
                  <div className="gross-modal-item"><span>Medical Allowance</span><strong>₹{fmtINR(compHistory.salaryBreakdown.medical)}</strong></div>
                  <div className="gross-modal-item"><span>Telephone &amp; Internet</span><strong>₹{fmtINR(compHistory.salaryBreakdown.telephone)}</strong></div>
                  <div className="gross-modal-item"><span>Special Allowance</span><strong>₹{fmtINR(compHistory.salaryBreakdown.special)}</strong></div>
                  <div className="gross-modal-item gross-modal-highlight"><span>Monthly Gross</span><strong>₹{fmtINR(parseCTCNumber(String(compHistory.grossMonthlySalary || "0")))}</strong></div>
                  <div className="gross-modal-item"><span>Annual Gross</span><strong>₹{fmtINR(parseCTCNumber(String(compHistory.grossMonthlySalary || "0")) * 12)}</strong></div>
                </div>

                <div className="gross-modal-section-label">Variable &amp; One-time Components</div>
                <div className="gross-modal-grid">
                  <div className="gross-modal-item"><span>Joining Bonus</span><strong>₹{fmtINR(compHistory.variableComponents?.joiningBonus || 0)}</strong></div>
                  <div className="gross-modal-item"><span>Variable Pay Pool (Annual Max)</span><strong>₹{fmtINR(compHistory.variableComponents?.variablePayPool || 0)}</strong></div>
                  <div className="gross-modal-item"><span>Retention Bonus Pool</span><strong>₹{fmtINR(compHistory.variableComponents?.retentionPool || 0)}</strong></div>
                  {Number(compHistory.variableComponents?.retentionPool) > 0 && (
                    <>
                      <div className="gross-modal-item"><span>Retention Tranche 1</span><strong>₹{fmtINR(compHistory.variableComponents?.t1 || 0)}</strong></div>
                      <div className="gross-modal-item"><span>Retention Tranche 2</span><strong>₹{fmtINR(compHistory.variableComponents?.t2 || 0)}</strong></div>
                      <div className="gross-modal-item"><span>Retention Tranche 3</span><strong>₹{fmtINR(compHistory.variableComponents?.t3 || 0)}</strong></div>
                      <div className="gross-modal-item"><span>Retention Tranche 4</span><strong>₹{fmtINR(compHistory.variableComponents?.t4 || 0)}</strong></div>
                      <div className="gross-modal-item"><span>Retention Tranche 5</span><strong>₹{fmtINR(compHistory.variableComponents?.t5 || 0)}</strong></div>
                    </>
                  )}
                </div>
                {Number(compHistory.variableComponents?.variablePayPool) > 0 && (
                  <p className="gross-breakdown-note">Max committed pool — actual payout = Pool × Performance KPI%, released at year-end.</p>
                )}

                <div className="gross-modal-total">
                  <span>Total CTC</span>
                  <strong>₹{fmtINR(parseCTCNumber(String(compHistory.annualCTC || "0")))}</strong>
                </div>
              </>
            ) : (
              <div className="gross-breakdown-empty">
                <p>No fixed salary breakdown saved for this employee yet.</p>
                {canAppraise && (
                  <button type="button" className="gross-backfill-btn" onClick={handleBackfillBreakdown} disabled={backfilling}>
                    {backfilling ? "Saving…" : "Generate & Save from Current Gross"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AppraisalModal */}
      {showAppraisalModal && (
        <AppraisalModal
          // pass merged record so modal has the same salary/variable data shown in profile
          employee={{
            ...employee,
            salaryBreakdown: compHistory?.salaryBreakdown || employee.salaryBreakdown,
            variableComponents: compHistory?.variableComponents || employee.variableComponents,
            grossMonthlySalary: compHistory?.grossMonthlySalary || employee.grossMonthlySalary,
            annualCTC: compHistory?.annualCTC || employee.annualCTC,
          }}
          onClose={() => setShowAppraisalModal(false)}
          onAppraisalDone={handleAppraisalDone}
          onPromoteDone={handlePromoteDone}
        />
      )}
    </div>
  );
};

export default EmployeeProfile;
